import React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  className?: string;
}

/**
 * Apple-inspired authentication layout component
 * Provides consistent styling and animations for all auth pages
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  subtitle,
  className,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={cn(
          'w-full max-w-md space-y-8',
          className
        )}
      >
        {/* Logo and Brand */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-center"
        >
          <div className="mx-auto h-20 w-20 flex items-center justify-center mb-6">
            <img 
              src="/logo.svg" 
              alt="Qodrix Menu" 
              className="h-20 w-20 object-contain"
            />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 font-apple mb-2">
            {title}
          </h1>
          
          {subtitle && (
            <p className="text-gray-600 font-apple text-base">
              {subtitle}
            </p>
          )}
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-apple-lg p-8 border border-gray-100"
        >
          {children}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="text-center text-sm text-gray-500 font-apple"
        >
          <p>
            Qodrix Menu - Digital Menu SaaS for Restaurants
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AuthLayout;